/**
 * The few marks the map and the scenario lab need that the positions board
 * does not: an actor with no recorded position, the balance bar with its unread
 * slice, and an index that can show where it moved from.
 *
 * Everything else — the chevron glyphs, pills and ladder — comes from
 * `../components/PositionMark`, so the two surfaces stay one language.
 */

import React from 'react';
import { CircleDashed } from 'lucide-react';
import { PositionGlyph, PositionLadder, PositionPill } from '../components/PositionMark';
import { POSITION_ORDER, balanceSegments, isUnknown, positionStyle, signed } from './position';
import type { BalanceSegment } from './position';
import type { BalanceOfForces } from '../../../types/issues';

/* ------------------------------------------------------------------ */
/* Unknown-aware marks                                                 */
/* ------------------------------------------------------------------ */

/** The board's glyph, plus a dashed ring for the actors nobody has read */
export const StanceGlyph: React.FC<{ position: number | null | undefined; className?: string }> = ({ position, className = '' }) => {
  if (isUnknown(position)) {
    return (
      <span
        title="No position recorded"
        className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-dashed border-neutral-300 text-neutral-400 ${className}`}
      >
        <CircleDashed className="h-3 w-3" strokeWidth={1.75} />
        <span className="sr-only">No position recorded</span>
      </span>
    );
  }
  return <PositionGlyph position={position as number} className={className} />;
};

/** The board's pill, plus an outline chip for the unread */
export const StancePill: React.FC<{ position: number | null | undefined; short?: boolean; className?: string }> = ({
  position,
  short = false,
  className = '',
}) => {
  if (isUnknown(position)) {
    return (
      <span
        className={`inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-neutral-300 pl-1.5 pr-2 text-[11px] leading-none text-neutral-500 ${className}`}
      >
        <CircleDashed className="h-3 w-3" strokeWidth={1.75} />
        No position
      </span>
    );
  }
  return <PositionPill position={position as number} short={short} className={className} />;
};

/** The five-step ladder, blank when there is nothing to place on it */
export const StanceLadder: React.FC<{ position: number | null | undefined; className?: string }> = ({ position, className = '' }) => {
  if (isUnknown(position)) {
    return (
      <span className={`inline-flex items-end gap-[2px] ${className}`} aria-hidden="true">
        {POSITION_ORDER.map((step) => (
          <span key={step} className="block h-1.5 w-[3px] rounded-full bg-neutral-100 ring-1 ring-inset ring-neutral-200" />
        ))}
      </span>
    );
  }
  return <PositionLadder position={position as number} className={className} />;
};

/** The key, including the state the board never has to show */
export const StanceLegend: React.FC<{
  counts?: Partial<Record<number | 'unknown', number>>;
  className?: string;
}> = ({ counts, className = '' }) => (
  <ul className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 ${className}`}>
    {POSITION_ORDER.map((position) => (
      <li key={position} className="inline-flex items-center gap-1.5 text-[11.5px] text-neutral-500">
        <StanceGlyph position={position} />
        {positionStyle(position).short}
        {counts?.[position] !== undefined && <span className="tabular-nums text-neutral-400">{counts[position]}</span>}
      </li>
    ))}
    <li className="inline-flex items-center gap-1.5 text-[11.5px] text-neutral-500">
      <StanceGlyph position={null} />
      No position
      {counts?.unknown !== undefined && <span className="tabular-nums text-neutral-400">{counts.unknown}</span>}
    </li>
  </ul>
);

/* ------------------------------------------------------------------ */
/* Balance of forces                                                   */
/* ------------------------------------------------------------------ */

const hatch = (hex: string) =>
  `repeating-linear-gradient(135deg, rgba(255,255,255,0.45) 0 2px, rgba(255,255,255,0) 2px 5px), linear-gradient(${hex}, ${hex})`;

const Segment: React.FC<{ segment: BalanceSegment; animate: boolean }> = ({ segment, animate }) => (
  <span
    className={`block h-full ${animate ? 'transition-[width] duration-700 ease-out motion-reduce:transition-none' : ''}`}
    style={{
      width: `${segment.pct}%`,
      backgroundColor: segment.hex,
      backgroundImage: segment.hatched ? hatch(segment.hex) : undefined,
    }}
    title={`${segment.label} — ${Math.round(segment.pct)}% of the influence in play`}
  />
);

/**
 * Influence in play, split four ways, in board order: opposition hatched on the
 * left, support solid on the right, unread weight last.
 */
export const ForcesBar: React.FC<{
  balance: BalanceOfForces | null | undefined;
  className?: string;
  animate?: boolean;
  showKey?: boolean;
}> = ({ balance, className = '', animate = true, showKey = true }) => {
  const segments = balanceSegments(balance);
  const empty = segments.every((s) => s.pct <= 0);
  return (
    <div className={className}>
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-neutral-100"
        role="img"
        aria-label={
          empty
            ? 'No influence recorded yet'
            : segments.map((s) => `${s.label} ${Math.round(s.pct)}%`).join(', ')
        }
      >
        {segments.filter((s) => s.pct > 0).map((segment) => (
          <Segment key={segment.key} segment={segment} animate={animate} />
        ))}
      </div>
      {showKey && !empty && (
        <ul className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-neutral-500">
          {segments.map((segment) => (
            <li key={segment.key} className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: segment.hex, backgroundImage: segment.hatched ? hatch(segment.hex) : undefined }}
              />
              {segment.label}
              <span className="tabular-nums text-neutral-400">{Math.round(segment.pct)}%</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * The index on its −100…+100 rail. With `before` set the marker slides from
 * where it was and leaves a ghost behind: two numbers side by side hide the
 * size of a move, a marker that travels does not.
 */
export const IndexTrack: React.FC<{
  index: number;
  before?: number | null;
  className?: string;
  compact?: boolean;
}> = ({ index, before = null, className = '', compact = false }) => {
  const at = (value: number) => `${((Math.max(-100, Math.min(100, value || 0)) + 100) / 200) * 100}%`;
  const moved = before !== null && before !== undefined && Math.round(before) !== Math.round(index);
  return (
    <div className={className}>
      <div
        className="relative h-6"
        role="img"
        aria-label={moved ? `Balance ${signed(before as number)} → ${signed(index)}` : `Balance ${signed(index)}`}
      >
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-neutral-100">
          <div
            className="h-full w-full"
            style={{ backgroundImage: 'linear-gradient(to right, #fecaca, #f5f5f5 45%, #f5f5f5 55%, #a7f3d0)' }}
          />
        </div>
        <span className="absolute left-1/2 top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-neutral-300" aria-hidden="true" />
        {moved && (
          <>
            <span
              className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-neutral-300"
              style={{
                left: `min(${at(before as number)}, ${at(index)})`,
                width: `calc(max(${at(before as number)}, ${at(index)}) - min(${at(before as number)}, ${at(index)}))`,
              }}
              aria-hidden="true"
            />
            <span
              className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-neutral-400 bg-white"
              style={{ left: at(before as number) }}
              title={`Before ${signed(before as number)}`}
            />
          </>
        )}
        <span
          className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-ink shadow-[0_1px_4px_rgba(0,0,0,0.18)] transition-[left] duration-700 ease-out motion-reduce:transition-none"
          style={{ left: at(index) }}
          title={`Now ${signed(index)}`}
        />
      </div>
      {!compact && (
        <div className="flex items-center justify-between text-[11px] text-neutral-400">
          <span>All against</span>
          <span>All behind us</span>
        </div>
      )}
    </div>
  );
};
